import { useMemo, useRef, useState } from "react";
import moment from "moment";
import { store } from "../../source/index";
import { toUnformatPrice } from "../common/helpers/function";

/* Client-side column sorting for the inventory reports (no jQuery DataTables).
 * `accessors` is indexed like the table columns (index 0 is "#"); a column
 * without an accessor is not sortable. Sorting applies to the loaded page. */

const DATE_FORMATS = ["DD/MM/YYYY", "YYYY/MM/DD", "MM/DD/YYYY", "YYYY-MM-DD", "DD-MM-YYYY"];
const DATE_RE = /^\d{1,4}[/.-]\d{1,2}[/.-]\d{1,4}/;
const NUMBER_RE = /^[^\d\s-]{0,4}\s?-?[\d.,]+$/;

const toSortable = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return value;
  const text = String(value).trim();
  if (!text || text === "-") return null;

  const dateMatch = text.match(DATE_RE);
  if (dateMatch) {
    const orgFormat = store.getState()?.app?.organisation_settings?.date_format;
    const parsed = moment(dateMatch[0], [orgFormat, ...DATE_FORMATS].filter(Boolean), true);
    if (parsed.isValid()) return parsed.valueOf();
  }
  if (NUMBER_RE.test(text)) {
    const number = toUnformatPrice(text);
    if (Number.isFinite(number)) return number;
  }
  return text.toLowerCase();
};

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

export const useReportSort = (list, accessors) => {
  const [sortConfig, setSortConfig] = useState({ index: null, direction: "asc" });
  // accessors are passed inline by each report; keep the latest without re-sorting every render
  const accessorsRef = useRef(accessors);
  accessorsRef.current = accessors;

  const handleSort = (index) => {
    if (!accessorsRef.current?.[index]) return;
    setSortConfig((prev) =>
      prev.index === index
        ? { index, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { index, direction: "asc" }
    );
  };

  const sortedList = useMemo(() => {
    const accessor = accessorsRef.current?.[sortConfig.index];
    if (!accessor || !Array.isArray(list)) return list;
    const factor = sortConfig.direction === "desc" ? -1 : 1;
    return list
      .map((row) => ({ row, value: toSortable(accessor(row)) }))
      .sort((a, b) => {
        // empty values always go last
        if (a.value === null) return b.value === null ? 0 : 1;
        if (b.value === null) return -1;
        return compareValues(a.value, b.value) * factor;
      })
      .map(({ row }) => row);
  }, [list, sortConfig]);

  return { sortedList, sortConfig, handleSort };
};
